import { useAtsApplicationScoringApi } from '@/composables/useApi';
import type { ATSApplicationScoringProScoring, JobApplicant } from '@factoryfixinc/ats-interfaces';
import { InternalError } from '@/core/shared/errors/internal.error';
import { HttpStatus } from '@/core/shared/errors/enums/http-status.enum';
import { useApplicationScoreStore } from '@/core/conversations/application-score/application-score.store';
import { ErrorService } from '@/core/shared/errors/error.service';

export default class ApplicationScorePersistence {
  private applicationScoreStore = useApplicationScoreStore();

  public async fetchScoreDetails(
    jobApplicationId: JobApplicant['id'],
  ): Promise<ATSApplicationScoringProScoring | undefined> {
    const url = `/score?jobApplicationId=${jobApplicationId}`;
    const { data, error, statusCode } = await useAtsApplicationScoringApi(url)
      .get()
      .json<ATSApplicationScoringProScoring>();

    if (statusCode.value === HttpStatus.NOT_FOUND || !data.value) {
      return;
    }

    if (error.value) {
      throw new InternalError('Failed to fetch score details', {
        error: error.value,
        status: statusCode.value,
        data: { jobApplicationId },
      });
    }

    this.applicationScoreStore.setScoreDetail(data.value);
    return data.value;
  }

  public async submitScoreFeedback(
    employerId: number,
    params: {
      jobApplicantId: number;
      originalScore: number;
      originalSummary: string;
      feedback: 'thumbsup' | 'thumbsdown';
      userScore?: number;
      comments?: string;
    },
  ): Promise<{ feedbackId: string; currentCount: number; threshold: number }> {
    try {
      const { data, error, statusCode } = await useAtsApplicationScoringApi(
        `/score-feedback/employer/${employerId}/jobApplicant/${params.jobApplicantId}/submit`,
      )
        .post(params)
        .json<{ feedbackId: string; currentCount: number; threshold: number }>();

      if (error.value || !data.value) {
        throw new InternalError('Failed to submit score feedback', {
          error: error.value,
          status: statusCode.value,
          data: params,
        });
      }

      return data.value;
    } catch (error) {
      ErrorService.captureException(error);
      throw error;
    }
  }

  public async getFeedbackCount(
    employerId: number,
    jobId: number,
  ): Promise<{ currentCount: number; threshold: number }> {
    try {
      const url = `/score-feedback/employer/${employerId}/job/${jobId}/count`;
      const { data, error, statusCode } = await useAtsApplicationScoringApi(url)
        .get()
        .json<{ currentCount: number; threshold: number }>();

      if (error.value || !data.value) {
        throw new InternalError('Failed to fetch feedback count', {
          error: error.value,
          status: statusCode.value,
          data: { jobId, employerId },
        });
      }

      return data.value;
    } catch (error) {
      ErrorService.captureException(error);
      throw error;
    }
  }
}
